import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid2 as Grid,
  Chip,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  InputAdornment,
  CircularProgress,
  Alert,
  Link,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Description as PageIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Store as StoreIcon,
  ShoppingCart as EcommerceIcon,
  EventNote as EventoIcon,
  Payment as RecargaIcon,
  Inventory2 as PacotesIcon,
} from '@mui/icons-material';
import api from '../../services/api';

interface PaymentPage {
  id: string;
  titulo: string;
  descricao: string;
  tipo: string;
  tipoJson?: string;
  url?: string;
  urlWeb?: string;
  morada?: string;
  entidadeServicosId?: string;
  entidadeId?: string;
  entidadeNome?: string;
  estado: string;
  dataRegisto: string;
}

interface PageStats {
  total: number;
  ativos: number;
  inativos: number;
  ecommerce?: number;
  recargas?: number;
  eventos?: number;
}

const TIPOS_TEMPLATE = [
  { value: 'ECOMMERCE', label: 'E-Commerce', icon: <EcommerceIcon fontSize="small" /> },
  { value: 'RECARGA', label: 'Recarga', icon: <RecargaIcon fontSize="small" /> },
  { value: 'EVENTO', label: 'Eventos', icon: <EventoIcon fontSize="small" /> },
  { value: 'SERVICO', label: 'Serviços', icon: <StoreIcon fontSize="small" /> },
  { value: 'DONATIVO', label: 'Donativos', icon: <PageIcon fontSize="small" /> },
  { value: 'STANDARD', label: 'Padrão', icon: <PageIcon fontSize="small" /> },
];

const PaymentPages: React.FC = () => {
  const [pages, setPages] = useState<PaymentPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState<PageStats>({ total: 0, ativos: 0, inativos: 0 });
  const [editDialog, setEditDialog] = useState(false);
  const [previewDialog, setPreviewDialog] = useState(false);
  const [selectedPage, setSelectedPage] = useState<PaymentPage | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterTipo, setFilterTipo] = useState<string>('');
  const [formData, setFormData] = useState({
    titulo: '',
    descricao: '',
    tipo: 'STANDARD',
    url: '',
    urlWeb: '',
    morada: '',
    estado: 'A',
  });

  // Pacotes (valores fixos)
  const [pacotesDialog, setPacotesDialog] = useState(false);
  const [pacotesPage, setPacotesPage] = useState<PaymentPage | null>(null);
  const [pacotesList, setPacotesList] = useState<Array<{ id: string; label: string; valor: number; ordem: number }>>([]);
  const [pacoteForm, setPacoteForm] = useState<{ id?: string; label: string; valor: string; ordem: string }>({ label: '', valor: '', ordem: '0' });
  const [pacotesLoading, setPacotesLoading] = useState(false);

  const fetchPacotes = useCallback(async (templateId: string) => {
    setPacotesLoading(true);
    try {
      const r = await api.get(`/admin/payment-pages/${templateId}/pacotes`);
      setPacotesList(r.data?.data || []);
    } catch (e: any) {
      setPacotesList([]);
    } finally {
      setPacotesLoading(false);
    }
  }, []);

  const openPacotes = (paymentPage: PaymentPage) => {
    setPacotesPage(paymentPage);
    setPacoteForm({ label: '', valor: '', ordem: '0' });
    setPacotesDialog(true);
    fetchPacotes(paymentPage.id);
  };

  const savePacote = async () => {
    if (!pacotesPage || !pacoteForm.label.trim() || !pacoteForm.valor) return;
    try {
      if (pacoteForm.id) {
        await api.put(`/admin/payment-pages/pacotes/${pacoteForm.id}`, {
          label: pacoteForm.label.trim(),
          valor: parseFloat(pacoteForm.valor),
          ordem: parseInt(pacoteForm.ordem) || 0,
        });
      } else {
        await api.post(`/admin/payment-pages/${pacotesPage.id}/pacotes`, {
          label: pacoteForm.label.trim(),
          valor: parseFloat(pacoteForm.valor),
          ordem: parseInt(pacoteForm.ordem) || 0,
        });
      }
      setPacoteForm({ label: '', valor: '', ordem: '0' });
      fetchPacotes(pacotesPage.id);
    } catch (e: any) {
      setError(e?.message || 'Erro ao guardar pacote');
    }
  };

  const editPacote = (p: { id: string; label: string; valor: number; ordem: number }) => {
    setPacoteForm({ id: p.id, label: p.label, valor: String(p.valor), ordem: String(p.ordem) });
  };

  const deletePacote = async (id: string) => {
    if (!pacotesPage) return;
    if (!window.confirm('Eliminar este pacote?')) return;
    try {
      await api.delete(`/admin/payment-pages/pacotes/${id}`);
      fetchPacotes(pacotesPage.id);
    } catch (e: any) {
      setError(e?.message || 'Erro ao remover pacote');
    }
  };

  // Entity/service selector for create
  const [entityList, setEntityList] = useState<any[]>([]);
  const [entityServices, setEntityServices] = useState<any[]>([]);
  const [selectedEntityId, setSelectedEntityId] = useState('');
  const [selectedServiceId, setSelectedServiceId] = useState('');
  const [loadingEntities, setLoadingEntities] = useState(false);
  const [loadingServices, setLoadingServices] = useState(false);

  const fetchPages = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: String(page + 1),
        limit: String(rowsPerPage),
        ...(searchTerm && { search: searchTerm }),
        ...(filterStatus && { status: filterStatus }),
        ...(filterTipo && { tipo: filterTipo }),
      });

      const response = await api.get(`/admin/payment-pages?${params}`);
      if (response.data.success) {
        setPages(response.data.data || []);
        setTotalCount(response.data.pagination?.total || 0);
        if (response.data.stats) {
          setStats(response.data.stats);
        }
      }
    } catch (err: any) {
      console.error('Error fetching payment pages:', err);
      setError(err.message || 'Erro ao carregar páginas de pagamento');
      setPages([]);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm, filterStatus, filterTipo]);

  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  const fetchEntities = async () => {
    setLoadingEntities(true);
    try {
      const response = await api.get('/admin/entities?limit=100&status=A');
      if (response.data.success) setEntityList(response.data.data || []);
    } catch (err) {
      console.error('Error fetching entities:', err);
    } finally {
      setLoadingEntities(false);
    }
  };

  const fetchEntityServices = async (entityId: string) => {
    setLoadingServices(true);
    setEntityServices([]);
    setSelectedServiceId('');
    try {
      const response = await api.get(`/admin/entities/${entityId}`);
      if (response.data.success) {
        const services = response.data.data?.servicos || response.data.data?.services || [];
        setEntityServices(services);
      }
    } catch (err) {
      console.error('Error fetching entity services:', err);
    } finally {
      setLoadingServices(false);
    }
  };

  const getStatusChip = (estado: string) => {
    return estado === 'A' ? (
      <Chip size="small" color="success" label="Activo" icon={<ActiveIcon />} />
    ) : (
      <Chip size="small" color="default" label="Inactivo" icon={<InactiveIcon />} />
    );
  };

  const getTipoInfo = (tipo: string) => {
    const found = TIPOS_TEMPLATE.find(t => t.value === tipo);
    return found || { value: tipo, label: tipo, icon: <PageIcon fontSize="small" /> };
  };

  const handleOpenEdit = (paymentPage?: PaymentPage) => {
    if (paymentPage) {
      setSelectedPage(paymentPage);
      setFormData({
        titulo: paymentPage.titulo,
        descricao: paymentPage.descricao || '',
        tipo: paymentPage.tipo,
        url: paymentPage.url || '',
        urlWeb: paymentPage.urlWeb || '',
        morada: paymentPage.morada || '',
        estado: paymentPage.estado,
      });
    } else {
      setSelectedPage(null);
      setFormData({
        titulo: '',
        descricao: '',
        tipo: 'STANDARD',
        url: '',
        urlWeb: '',
        morada: '',
        estado: 'A',
      });
      setSelectedEntityId('');
      setSelectedServiceId('');
      setEntityServices([]);
      fetchEntities();
    }
    setEditDialog(true);
  };

  const handleOpenPreview = async (paymentPage: PaymentPage) => {
    try {
      const response = await api.get(`/admin/payment-pages/${paymentPage.id}`);
      if (response.data.success) {
        setSelectedPage(response.data.data);
      } else {
        setSelectedPage(paymentPage);
      }
    } catch (err) {
      setSelectedPage(paymentPage);
    }
    setPreviewDialog(true);
  };

  const handleSave = async () => {
    try {
      setError(null);
      if (selectedPage) {
        await api.put(`/admin/payment-pages/${selectedPage.id}`, {
          titulo: formData.titulo,
          descricao: formData.descricao,
          tipo: formData.tipo,
          url: formData.url || null,
          urlWeb: formData.urlWeb || null,
          morada: formData.morada || null,
          estado: formData.estado,
        });
      } else {
        if (!selectedServiceId) {
          setError('Seleccione uma entidade e serviço para criar a página');
          return;
        }
        await api.post('/admin/payment-pages', {
          ...formData,
          entidadeServicosId: selectedServiceId,
        });
      }
      setEditDialog(false);
      setSelectedPage(null);
      fetchPages();
    } catch (err: any) {
      setError(err.message || 'Erro ao guardar página de pagamento');
    }
  };

  const handleDelete = async (paymentPage: PaymentPage) => {
    if (!window.confirm(`Eliminar página "${paymentPage.titulo}"?`)) {
      return;
    }

    try {
      setError(null);
      await api.delete(`/admin/payment-pages/${paymentPage.id}`);
      fetchPages();
    } catch (err: any) {
      setError(err.message || 'Erro ao eliminar página');
    }
  };

  if (loading && pages.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          Páginas de Pagamento
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenEdit()}>
            Nova Página
          </Button>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchPages}>
            Atualizar
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PageIcon color="primary" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.total}</Typography>
                  <Typography variant="caption" color="text.secondary">Total Páginas</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ActiveIcon color="success" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.ativos}</Typography>
                  <Typography variant="caption" color="text.secondary">Activas</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InactiveIcon color="disabled" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.inativos}</Typography>
                  <Typography variant="caption" color="text.secondary">Inactivas</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EcommerceIcon color="info" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.ecommerce || 0}</Typography>
                  <Typography variant="caption" color="text.secondary">E-Commerce</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ mb: 2, p: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              size="small"
              label="Pesquisar"
              placeholder="Título, descrição..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              size="small"
              select
              label="Estado"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="A">Activas</MenuItem>
              <MenuItem value="I">Inactivas</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              size="small"
              select
              label="Tipo"
              value={filterTipo}
              onChange={(e) => setFilterTipo(e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              {TIPOS_TEMPLATE.map((tipo) => (
                <MenuItem key={tipo.value} value={tipo.value}>
                  {tipo.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Título</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Entidade</TableCell>
                <TableCell width={120}>Estado</TableCell>
                <TableCell width={150} align="center">Acções</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pages.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      Nenhuma página encontrada
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                pages.map((paymentPage) => (
                  <TableRow key={paymentPage.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {paymentPage.titulo}
                      </Typography>
                      {paymentPage.descricao && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {paymentPage.descricao.substring(0, 60)}{paymentPage.descricao.length > 60 ? '...' : ''}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={getTipoInfo(paymentPage.tipo).label}
                        icon={getTipoInfo(paymentPage.tipo).icon}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {paymentPage.entidadeNome || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>{getStatusChip(paymentPage.estado)}</TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => handleOpenPreview(paymentPage)} title="Ver detalhes">
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleOpenEdit(paymentPage)} title="Editar">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => openPacotes(paymentPage)} title="Pacotes (valores fixos)">
                        <PacotesIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(paymentPage)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage="Linhas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </Paper>

      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PageIcon />
          {selectedPage ? 'Editar Página de Pagamento' : 'Nova Página de Pagamento'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            {/* Entity/Service selector - only for create */}
            {!selectedPage && (
              <>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    select
                    label="Entidade *"
                    value={selectedEntityId}
                    onChange={(e) => {
                      setSelectedEntityId(e.target.value);
                      if (e.target.value) fetchEntityServices(e.target.value);
                    }}
                    disabled={loadingEntities}
                  >
                    <MenuItem value="">Seleccione uma entidade</MenuItem>
                    {entityList.map((ent: any) => (
                      <MenuItem key={ent.id} value={ent.id}>{ent.desig || ent.nome}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    select
                    label="Serviço *"
                    value={selectedServiceId}
                    onChange={(e) => setSelectedServiceId(e.target.value)}
                    disabled={!selectedEntityId || loadingServices}
                  >
                    <MenuItem value="">{loadingServices ? 'A carregar...' : 'Seleccione um serviço'}</MenuItem>
                    {entityServices.map((svc: any) => (
                      <MenuItem key={svc.id} value={svc.id}>{svc.nome || svc.name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </>
            )}
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Título"
                value={formData.titulo}
                onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Descrição"
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label="Tipo"
                value={formData.tipo}
                onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
              >
                {TIPOS_TEMPLATE.map((tipo) => (
                  <MenuItem key={tipo.value} value={tipo.value}>
                    {tipo.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label="Estado"
                value={formData.estado}
                onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
              >
                <MenuItem value="A">Activo</MenuItem>
                <MenuItem value="I">Inactivo</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Morada"
                value={formData.morada}
                onChange={(e) => setFormData({ ...formData, morada: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="URL"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="URL Web"
                value={formData.urlWeb}
                onChange={(e) => setFormData({ ...formData, urlWeb: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!formData.titulo}
          >
            {selectedPage ? 'Guardar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={previewDialog} onClose={() => setPreviewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Detalhes da Página de Pagamento</DialogTitle>
        <DialogContent dividers>
          {selectedPage && (
            <Box>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="caption" color="text.secondary">Título</Typography>
                  <Typography variant="body1" fontWeight={600}>{selectedPage.titulo}</Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="caption" color="text.secondary">Descrição</Typography>
                  <Typography variant="body1">{selectedPage.descricao || '-'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography variant="caption" color="text.secondary">Tipo</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      size="small"
                      label={getTipoInfo(selectedPage.tipo).label}
                      icon={getTipoInfo(selectedPage.tipo).icon}
                      variant="outlined"
                    />
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography variant="caption" color="text.secondary">Estado</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    {getStatusChip(selectedPage.estado)}
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography variant="caption" color="text.secondary">Entidade</Typography>
                  <Typography variant="body1">{selectedPage.entidadeNome || '-'}</Typography>
                </Grid>
                {selectedPage.morada && (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="caption" color="text.secondary">Morada</Typography>
                    <Typography variant="body1">{selectedPage.morada}</Typography>
                  </Grid>
                )}
                {(selectedPage.url || selectedPage.urlWeb) && (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="caption" color="text.secondary">URLs</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {selectedPage.url && (
                        <Link href={selectedPage.url} target="_blank" rel="noopener">
                          {selectedPage.url}
                        </Link>
                      )}
                      {selectedPage.urlWeb && (
                        <Link href={selectedPage.urlWeb} target="_blank" rel="noopener">
                          {selectedPage.urlWeb}
                        </Link>
                      )}
                    </Box>
                  </Grid>
                )}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="caption" color="text.secondary">Data de Registo</Typography>
                  <Typography variant="body1">
                    {selectedPage.dataRegisto ? new Date(selectedPage.dataRegisto).toLocaleString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' }) : '-'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialog(false)}>Fechar</Button>
          <Button variant="contained" onClick={() => {
            setPreviewDialog(false);
            if (selectedPage) handleOpenEdit(selectedPage);
          }}>
            Editar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pacotes Dialog (valores fixos pré-estabelecidos) */}
      <Dialog open={pacotesDialog} onClose={() => setPacotesDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Pacotes — {pacotesPage?.titulo || ''}
          <Typography variant="caption" display="block" color="text.secondary">
            Valores fixos apresentados ao cliente para esta página de pagamento
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 3, display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            <TextField
              label="Descrição"
              size="small"
              value={pacoteForm.label}
              onChange={(e) => setPacoteForm({ ...pacoteForm, label: e.target.value })}
              sx={{ flex: 2 }}
              placeholder="Ex: Semanal 1.000MB"
            />
            <TextField
              label="Valor (CVE)"
              size="small"
              type="number"
              value={pacoteForm.valor}
              onChange={(e) => setPacoteForm({ ...pacoteForm, valor: e.target.value })}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Ordem"
              size="small"
              type="number"
              value={pacoteForm.ordem}
              onChange={(e) => setPacoteForm({ ...pacoteForm, ordem: e.target.value })}
              sx={{ width: 90 }}
            />
            <Button variant="contained" onClick={savePacote}>
              {pacoteForm.id ? 'Atualizar' : 'Adicionar'}
            </Button>
            {pacoteForm.id && (
              <Button onClick={() => setPacoteForm({ label: '', valor: '', ordem: '0' })}>Cancelar</Button>
            )}
          </Box>

          {pacotesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : pacotesList.length === 0 ? (
            <Alert severity="info">Nenhum pacote configurado. Adicione acima.</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={60}>Ordem</TableCell>
                    <TableCell>Descrição</TableCell>
                    <TableCell align="right">Valor (CVE)</TableCell>
                    <TableCell align="center" width={100}>Acções</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pacotesList.map((p) => (
                    <TableRow key={p.id} hover>
                      <TableCell>{p.ordem}</TableCell>
                      <TableCell>{p.label}</TableCell>
                      <TableCell align="right">{Number(p.valor).toLocaleString('pt-PT')}</TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => editPacote(p)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => deletePacote(p.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPacotesDialog(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentPages;
